import _classCallCheck from "/home/hyuto/Hyuto.github.io/v3-beta/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/home/hyuto/Hyuto.github.io/v3-beta/node_modules/@babel/runtime/helpers/esm/createClass.js";

/**
 * @license
 * Copyright 2018 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import { useShapeUniforms } from './gpgpu_math';
import { getChannels } from './packing_util';
import { getCoordsDataType } from './shader_compiler';
export var PackProgram = /*#__PURE__*/function () {
  function PackProgram(outputShape) {
    _classCallCheck(this, PackProgram);

    this.variableNames = ['A'];
    this.packedInputs = false;
    this.packedOutput = true; // Only input / output 3D tensors.

    this.outputShape = outputShape;
    this.rank = outputShape.length;
    this.enableShapeUniforms = useShapeUniforms(this.outputShape.length);

    if (this.rank === 0) {
      this.userCode = "\n        void main() {\n          setOutput(vec4(getA(), 0., 0., 0.));\n        }\n      ";
    } else {
      var channels = getChannels('rc', this.rank);
      var dtype = getCoordsDataType(this.rank);
      var outOfBoundsCondition = this.getOutOfBoundsCondition(channels);
      var setup = this.getSetup(channels);
      var output = this.getOutput(channels);
      this.userCode = "\n        void main() {\n          ".concat(dtype, " rc = getOutputCoords();\n\n          if(").concat(outOfBoundsCondition, ") {\n            setOutput(vec4(0));\n          } else {\n            ").concat(setup, "\n\n            setOutput(vec4(").concat(output, "));\n          }\n        }\n      ");
    }
  }

  _createClass(PackProgram, [{
    key: "getSourceCoordsArr",
    value: function getSourceCoordsArr(dims) {
      var coords = [];

      for (var row = 0; row <= 1; row++) {
        for (var col = 0; col <= 1; col++) {
          var coord = "".concat(row === 0 ? 'r' : 'rp1', ", ").concat(col === 0 ? 'c' : 'cp1');

          for (var d = 2; d < this.rank; d++) {
            coord = "".concat(dims[dims.length - 1 - d], ",") + coord;
          }

          coords.push(coord);
        }
      }

      return coords;
    }
  }, {
    key: "getOutOfBoundsCondition",
    value: function getOutOfBoundsCondition(dims) {
      if (this.rank === 1) {
        return "rc > ".concat(this.enableShapeUniforms ? 'outShape' : this.outputShape[0]);
      }

      var cond = '';

      for (var i = this.rank - 2; i < this.rank; i++) {
        cond += "".concat(dims[i], " >= ").concat(this.enableShapeUniforms ? "outShape[".concat(i, "]") : this.outputShape[i]);

        if (i < this.rank - 1) {
          cond += '||';
        }
      }

      return cond;
    }
  }, {
    key: "getSetup",
    value: function getSetup(dims) {
      if (this.rank === 1) {
        return '';
      }

      var innerDims = dims.slice(-2);
      var col = this.enableShapeUniforms ? "outShape[".concat(this.rank, " - 1]") : this.outputShape[this.rank - 1];
      var row = this.enableShapeUniforms ? "outShape[".concat(this.rank, " - 2]") : this.outputShape[this.rank - 2];
      return "\n      int r = ".concat(innerDims[0], ";\n      int c = ").concat(innerDims[1], ";\n      int rp1 = r + 1;\n      int cp1 = c + 1;\n\n      bool cEdge = cp1 >= ").concat(col, ";\n      bool rEdge = rp1 >= ").concat(row, ";\n    ");
    }
  }, {
    key: "getOutput",
    value: function getOutput(dims) {
      var sourceCoords = this.getSourceCoordsArr(dims);

      if (this.rank === 1) {
        var outShape = this.enableShapeUniforms ? 'outShape' : this.outputShape[0];
        return "getA(rc), (rc + 1 >= ".concat(outShape, " ? 0. : getA(rc + 1)), 0, 0");
      }

      return "getA(".concat(sourceCoords[0], "),\n            cEdge ? 0. : getA(").concat(sourceCoords[1], "),\n            rEdge ? 0. : getA(").concat(sourceCoords[2], "),\n            rEdge || cEdge ? 0. : getA(").concat(sourceCoords[3], ")");
    }
  }]);

  return PackProgram;
}();